// Овертаймы и командировки — зеркало бытового блока портала 73. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ОТВЕЧАЕТ (три вопроса, ради которых он и сделан):
//   • сколько овертаймов за период и на какую сумму;
//   • что согласовано, а что ждёт — и чего ждёт: согласования или ведомости;
//   • чьи командировки открыты прямо сейчас.
// Всё остальное — фон. Каршеринг здесь третьей вкладкой, а не отдельным разделом: он
// живой (1 459 поездок на 28.07.2026), но инженеру в CRM важно одно — кто сейчас в пути.
//
// ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Такси: в источнике три записи за две недели, все не
// компенсированы. Экран над ними показывал бы, что на такси не ездят, — а ездят и
// проводят это авансовыми отчётами (вкладка «Командировки», расходы «Такси/трансфер»).
// Ручка в ядре есть (/p73x/taxi), экрана нет намеренно.
//
// ПРО ДЕНЬГИ. Здесь заработок конкретных людей. Ядро не отдаёт список по всем без явного
// all=1 и помечает такой ответ restricted='owner'; экран эту пометку ПОКАЗЫВАЕТ — человек
// должен видеть, что смотрит на чужие деньги, а не думать, что это обычный отчёт.
import { useMemo, useState } from 'react'

import {
  useP73XRefs, useКомандировки, useОвертаймы, useПоездки, useСводОвертаймов,
} from '../api/p73x'
import type { КомандировкаСтрока, ОвертаймСтрока, ПоездкаСтрока, СводСтрока } from '../api/p73x'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, todayIso } from '../lib/format'
import {
  ВАЖНЫЕ_ОГОВОРКИ, ИМЯ_СУЩНОСТИ, РАЗМЕР_СТРАНИЦЫ, видОплаты, видПоездки, видСогласования,
  видСтраны, командировкаОткрыта, месяцСловами, похожеНаПробу, расходы, свежесть, текущийМесяц,
  часы, часыСловами,
} from '../lib/p73x'

type Вкладка = 'overtime' | 'trips' | 'cars'

export default function Overtime() {
  const [вкладка, setВкладка] = useState<Вкладка>('overtime')
  const refs = useP73XRefs()

  const св = свежесть(refs.data?.freshness)
  const счёт = refs.data?.counts

  return (
    <>
      <div className="page-head">
        <h1>Овертаймы и командировки</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${св.age_min < 1 ? 'только что' : `${Math.round(св.age_min)} мин назад`}` +
              ` (позже всех — ${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity})`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Здесь заработок людей:</b> часы, доплаты и суточные поимённо. Записи заводят
        инженеры на портале 73, в CRM только обзор.
      </div>

      {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
          «.filters > * { width: 100% }» ставит кнопки в столбец, и три вкладки съедают
          весь первый экран до единственного, ради чего его открыли. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([
          ['overtime', `Овертаймы${счёт ? ` · ${счёт.overtime}` : ''}`],
          ['trips', `Командировки${счёт ? ` · ${счёт.trips_real} из ${счёт.trips}` : ''}`],
          ['cars', `Каршеринг${счёт ? ` · в пути ${счёт.car_trips_open}` : ''}`],
        ] as [Вкладка, string][]).map(([код, подпись]) => (
          <button key={код} onClick={() => setВкладка(код)}
                  aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {вкладка === 'overtime' && <Овертаймы />}
        {вкладка === 'trips' && <Командировки />}
        {вкладка === 'cars' && <Каршеринг />}
        <Оговорки заметки={refs.data?.data_notes} />
      </QueryState>
    </>
  )
}

/** Оговорки из ядра. Показываются на всех вкладках: без них числа читаются неверно. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  const важные = ВАЖНЫЕ_ОГОВОРКИ.filter((к) => заметки[к])
  if (!важные.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {важные.map((к) => <li key={к}>{заметки[к]}</li>)}
      </ul>
    </div>
  )
}

/** Пометка «в ответе деньги нескольких людей». Показываем, а не прячем. */
function Закрыто({ почему }: { почему?: string }) {
  if (!почему) return null
  return (
    <div className="notice" style={{ marginBottom: 12, fontSize: 12 }}>
      Выборка по всем сотрудникам: {почему}. Раздел открыт не всем — не показывайте экран
      через плечо.
    </div>
  )
}

// ═══════════════════════════════ овертаймы ═══════════════════════════════

function Овертаймы() {
  const refs = useP73XRefs()
  const [месяц, setМесяц] = useState(текущийМесяц())
  const [регион, setРегион] = useState('')
  const [человек, setЧеловек] = useState('')
  const [ждут, setЖдут] = useState(false)
  const [свод, setСвод] = useState(false)
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    month: месяц || undefined,
    region: регион || undefined,
    engineer: человек ? Number(человек) : undefined,
    approved: ждут ? '0' : undefined,
    all: !человек,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [месяц, регион, человек, ждут, start])

  const список = useОвертаймы(свод ? { ...фильтры, limit: 1 } : фильтры)
  const сводка = useСводОвертаймов(свод ? { month: месяц || undefined, region: регион || undefined } : { month: '' })
  const данные = список.data
  const итоги = данные?.totals
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <Закрыто почему={данные?.restricted_why} />

      <div className="card" style={{ marginBottom: 12 }}>
        {/* Пять полей в столбец на телефоне закрывают собой ведомость целиком, поэтому
            блок сворачивается; что применено, видно в его заголовке. */}
        <FilterBar id="overtime">
          <div className="f">
            <label htmlFor="otm">Месяц</label>
            <select id="otm" value={месяц} onChange={(e) => сСброса(setМесяц)(e.target.value)}>
              <option value="">все, что есть</option>
              {Object.entries(refs.data?.months ?? {}).map(([м, n]) => (
                <option key={м} value={м}>{месяцСловами(м)} ({n})</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="otr">Регион</label>
            <select id="otr" value={регион} onChange={(e) => сСброса(setРегион)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(refs.data?.regions ?? {}).filter(([к]) => к && к !== '—')
                .map(([к, n]) => <option key={к} value={к}>{к} ({n})</option>)}
            </select>
          </div>
          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="otp">Инженер</label>
            <select id="otp" value={человек} onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
              <option value="">все (видны деньги всех)</option>
              {(refs.data?.people ?? []).filter((ч) => ч.d1_id !== null).map((ч) => (
                <option key={ч.d1_id} value={String(ч.d1_id)}>{ч.name} ({ч.n})</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
            <input type="checkbox" checked={ждут}
                   onChange={(e) => сСброса(setЖдут)(e.target.checked)} />
            только ждут согласования
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
            <input type="checkbox" checked={свод} onChange={(e) => setСвод(e.target.checked)} />
            свод по людям
          </label>
        </FilterBar>
        {(refs.data?.people ?? []).some((ч) => ч.no_account) && (
          <div className="hint" style={{ marginTop: 8 }}>
            У части людей нет учётки портала — их записи завели за них. В списке инженеров
            таких нет, но в общей выборке они есть.
          </div>
        )}
      </div>

      {итоги && !свод && <ПлиткиОвертаймов итоги={итоги} />}

      {свод ? (
        <QueryState isLoading={сводка.isLoading} error={сводка.error}>
          <ТаблицаСвода строки={сводка.data?.rows ?? []} />
        </QueryState>
      ) : (
        <QueryState isLoading={список.isLoading} error={список.error}>
          <ТаблицаОвертаймов строки={данные?.rows ?? []} />
          <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
        </QueryState>
      )}
    </>
  )
}

function ПлиткиОвертаймов({ итоги }: { итоги: NonNullable<ReturnType<typeof useОвертаймы>['data']>['totals'] }) {
  const ждут = (итоги.n ?? 0) - (итоги.approved_n ?? 0)
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">записей</div>
        <div className="t-sum">{итоги.n ?? 0}</div>
        <div className="t-label">людей: {итоги.people ?? 0}</div>
      </div>
      <div className="tile">
        <div className="t-label">часы: работа + дорога</div>
        <div className="t-sum">{часыСловами((итоги.work_hours ?? 0) + (итоги.road_hours ?? 0))}</div>
        <div className="t-label">из них дорога {часыСловами(итоги.road_hours ?? 0)}</div>
      </div>
      <div className="tile">
        <div className="t-label">начислено</div>
        <div className="t-sum">{fmtMoney(итоги.sum_rub ?? 0)}</div>
        <div className="t-label">оплачено записей: {итоги.paid_n ?? 0}</div>
      </div>
      <div className={ждут > 0 ? 'tile hot' : 'tile'}>
        <div className="t-label">ждут согласования</div>
        <div className="t-sum">{ждут}</div>
        <div className="t-label">согласовано: {итоги.approved_n ?? 0}</div>
      </div>
      {(итоги.flagged_n ?? 0) > 0 && (
        <div className="tile hot">
          <div className="t-label">портал пометил как спорные</div>
          <div className="t-sum">{итоги.flagged_n}</div>
          <div className="t-label">проверка портала, не CRM</div>
        </div>
      )}
    </div>
  )
}

function ТаблицаОвертаймов({ строки }: { строки: ОвертаймСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">За этот период записей нет</div>
        <div className="empty-hint">
          Записи овертаймов живут в портале только два последних месяца — за более ранние
          смотрите ведомости.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Дата</th>
            <th>Инженер</th>
            <th>Часы</th>
            <th className="num">Сумма</th>
            <th>Состояние</th>
            <th>Сделка и судно</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => {
            const оп = видОплаты(r.pay_status)
            const сг = видСогласования(r.approved)
            return (
              <tr key={r.d1_id}>
                <td data-label="Дата">
                  <div className="nowrap">{fmtDate(r.work_date)}</div>
                  <div className="hint">
                    {r.day_type ?? ''}{r.avr ? ` · АВР ${r.avr}` : ''}
                  </div>
                </td>
                <td data-label="Инженер">
                  <div>{r.driver_name ?? '—'}</div>
                  <div className="hint">
                    {[r.region, r.category].filter(Boolean).join(' · ') || '—'}
                  </div>
                </td>
                <td data-label="Часы">
                  <div className="nowrap">{часыСловами(часы(r))}</div>
                  {(r.duty || r.raid || r.trip_work_days || r.amort_km) ? (
                    <div className="hint">
                      {[r.duty ? `дежурство ${r.duty}` : '',
                        r.raid ? `рейд ${r.raid}` : '',
                        r.trip_work_days ? `командировка ${r.trip_work_days} дн` : '',
                        r.amort_km ? `аморт. ${r.amort_km} км` : ''
                      ].filter(Boolean).join(' · ')}
                    </div>
                  ) : null}
                </td>
                <td className="num" data-label="Сумма">{fmtMoney(r.sum_rub ?? 0)}</td>
                <td data-label="Состояние">
                  <span className={сг.класс}>{сг.ярлык}</span>{' '}
                  <span className={оп.класс} title={оп.смысл}>{оп.ярлык}</span>
                  {r.batch_title && (
                    <div className="hint">{r.batch_title}</div>
                  )}
                  {r.flagged && (
                    <div className="error" style={{ fontSize: 12 }}>
                      портал пометил: {r.flag_reason || 'без причины'}
                    </div>
                  )}
                </td>
                <td data-label="Сделка и судно">
                  {r.deal?.bitrix_id
                    ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
                    : <span className="muted" title={r.deal_raw ?? ''}>сделка не разобрана</span>}
                  {r.vessel?.name && (
                    <div style={{ fontSize: 12 }}>
                      <EntityLink kind="vessel" id={r.vessel.bitrix_id} title={r.vessel.name} />
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ТаблицаСвода({ строки }: { строки: СводСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">За этот период записей нет</div>
      </div>
    )
  }
  const всего = строки.reduce((s, r) => s + (r.sum_rub ?? 0), 0)
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Инженер</th>
            <th className="num">Записей</th>
            <th>Часы</th>
            <th className="num">Начислено</th>
            <th className="num">Не оплачено</th>
            <th>Согласовано</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={`${r.driver_name}-${r.region}`}>
              <td data-label="Инженер">
                <div>{r.driver_name ?? '—'}</div>
                <div className="hint">{r.region ?? 'регион не указан'}</div>
              </td>
              <td className="num" data-label="Записей">{r.n}</td>
              <td data-label="Часы" className="nowrap">
                {часыСловами((r.work_hours ?? 0) + (r.road_hours ?? 0))}
              </td>
              <td className="num" data-label="Начислено">{fmtMoney(r.sum_rub ?? 0)}</td>
              <td className="num" data-label="Не оплачено">{fmtMoney(r.unpaid_rub ?? 0)}</td>
              <td data-label="Согласовано">
                {r.approved_n} из {r.n}
                {r.approved_n < r.n && (
                  <div className="hint">ждёт {r.n - r.approved_n}</div>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td data-label="Итого"><b>Итого по {строки.length} чел.</b></td>
            <td className="num" data-label="Записей">
              <b>{строки.reduce((s, r) => s + r.n, 0)}</b>
            </td>
            <td data-label="Часы" />
            <td className="num" data-label="Начислено"><b>{fmtMoney(всего)}</b></td>
            <td className="num" data-label="Не оплачено">
              {fmtMoney(строки.reduce((s, r) => s + (r.unpaid_rub ?? 0), 0))}
            </td>
            <td data-label="Согласовано" />
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0} onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

// ═══════════════════════════════ командировки ═══════════════════════════════
//
// Данных мало и это сказано вслух: 19 записей, из них 8 настоящих поездок, 11 «денег под
// отчёт» и три пробы. Поэтому не таблица на пол-страницы, а список «кто в отъезде» —
// единственное, что по таким числам можно утверждать честно.

function Командировки() {
  const [толькоОткрытые, setТолькоОткрытые] = useState(true)
  const запрос = useКомандировки({ open: толькоОткрытые || undefined, limit: РАЗМЕР_СТРАНИЦЫ })
  const строки = запрос.data?.rows ?? []
  const сегодня = todayIso()

  return (
    <>
      <Закрыто почему={запрос.data?.restricted_why} />
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="trips">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={толькоОткрытые}
                   onChange={(e) => setТолькоОткрытые(e.target.checked)} />
            только открытые
          </label>
        </FilterBar>
        {/* Счётчик стоит ЗА блоком фильтров, а не внутри: на телефоне блок свёрнут, и
            внутри него «сколько всего» пропало бы вместе с полями. */}
        <div className="hint" style={{ marginTop: 8 }}>
          всего в зеркале: {запрос.data?.total ?? 0}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {!строки.length ? (
          <div className="card empty">
            <div className="empty-title">Открытых командировок нет</div>
            <div className="empty-hint">
              Раздел командировок в портале запустили в конце июня — записей пока
              девятнадцать на всю компанию.
            </div>
          </div>
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Кто и куда</th>
                  <th>Когда</th>
                  <th>Вид</th>
                  <th className="num">Суточные</th>
                  <th className="num">Аванс</th>
                  <th>Сделка и судно</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => <СтрокаКомандировки key={r.d1_id} r={r} сегодня={сегодня} />)}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </>
  )
}

function СтрокаКомандировки({ r, сегодня }: { r: КомандировкаСтрока; сегодня: string }) {
  const вид = видПоездки(r.trip_kind)
  const проба = похожеНаПробу(r.destination, r.deal_raw, r.purpose)
  const открыта = командировкаОткрыта(r, сегодня)
  const расх = расходы(r)
  return (
    <tr>
      <td data-label="Кто и куда">
        <div>{r.engineer_name ?? '—'}</div>
        <div className="hint">
          {r.destination || 'место не указано'}
          {r.vessel_name ? ` · ${r.vessel_name}` : ''}
        </div>
        {проба && (
          <div className="error" style={{ fontSize: 12 }}>
            похоже на пробную запись — в данных их три
          </div>
        )}
      </td>
      <td data-label="Когда">
        <div className="nowrap">
          {r.date_from ? fmtDate(r.date_from) : '—'}
          {r.date_to ? ` — ${fmtDate(r.date_to)}` : ''}
        </div>
        <div className="hint">
          {r.days ? `${r.days} дн` : 'срок не указан'}
          {открыта ? ' · открыта' : ''}
        </div>
      </td>
      <td data-label="Вид">
        <span className={вид.поездка ? 'badge' : 'badge work'} title={вид.смысл}>
          {вид.ярлык}
        </span>
        <div className="hint">
          {видСтраны(r.kind)}{r.status ? ` · ${r.status}` : ''}
        </div>
      </td>
      <td className="num" data-label="Суточные">
        {вид.поездка ? fmtMoney(r.perdiem_total ?? 0) : <span className="muted">—</span>}
        {вид.поездка && r.perdiem_rate ? (
          <div className="hint">{r.perdiem_rate} ₽/сут</div>
        ) : null}
      </td>
      <td className="num" data-label="Аванс">
        {fmtMoney(r.advance ?? 0)}
        {r.advance_requested ? (
          <div className="hint">
            запрошено {fmtMoney(r.advance_requested)}
          </div>
        ) : null}
        {расх ? (
          <div className="hint">расходы {fmtMoney(расх)}</div>
        ) : null}
      </td>
      <td data-label="Сделка и судно">
        {r.deal?.bitrix_id
          ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
          : <span className="muted">{r.deal_raw ? 'сделка не разобрана' : 'без сделки'}</span>}
        {r.vessel?.name && (
          <div style={{ fontSize: 12 }}>
            <EntityLink kind="vessel" id={r.vessel.bitrix_id} title={r.vessel.name} />
          </div>
        )}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ каршеринг ═══════════════════════════════
//
// Самый живой раздел бытового блока и самый простой вопрос к нему: кто сейчас в пути.
// Всё остальное (пробеги, заправки, балансы) считают в портале, и переносить сюда его
// расчёты значило бы завести второй ответ на тот же вопрос.

function Каршеринг() {
  const [толькоВПути, setТолькоВПути] = useState(true)
  const запрос = useПоездки({
    status: толькоВПути ? 'open' : undefined,
    limit: РАЗМЕР_СТРАНИЦЫ,
  })
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="cars">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={толькоВПути}
                   onChange={(e) => setТолькоВПути(e.target.checked)} />
            только в пути
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги?.people ? ` · водителей ${итоги.people}` : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {!строки.length ? (
          <div className="card empty">
            <div className="empty-title">
              {толькоВПути ? 'Сейчас в пути никого' : 'Поездок не найдено'}
            </div>
            <div className="empty-hint">
              Поездки закрываются в портале 73 при возврате машины; в CRM это видно
              в течение нескольких минут.
            </div>
          </div>
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Водитель</th>
                  <th>Машина</th>
                  <th>Выезд</th>
                  <th>Куда</th>
                  <th className="num">Км</th>
                  <th>Сделка</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => <СтрокаПоездки key={r.d1_id} r={r} />)}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </>
  )
}

function СтрокаПоездки({ r }: { r: ПоездкаСтрока }) {
  return (
    <tr>
      <td data-label="Водитель">
        <div>{r.driver_name ?? '—'}</div>
        <div className="hint">{r.region ?? 'регион не указан'}</div>
      </td>
      <td data-label="Машина">
        <div>{r.car_title || r.car_plate || '—'}</div>
        <div className="muted mono">
          {r.car_plate}{r.car_kind === 'personal' ? ' · личная' : ''}
        </div>
      </td>
      <td data-label="Выезд" className="nowrap">
        {fmtDate(r.date_out, true)}
        {r.date_back
          ? <div className="hint">вернулся {fmtDate(r.date_back, true)}</div>
          : <div className="badge work">в пути</div>}
      </td>
      <td data-label="Куда">
        {r.destination || <span className="muted">не указано</span>}
        {r.return_issues && (
          <div className="error" style={{ fontSize: 12 }}>при возврате: {r.return_issues}</div>
        )}
      </td>
      <td className="num" data-label="Км">
        {r.km ?? <span className="muted">—</span>}
        {r.flagged && <div className="error" style={{ fontSize: 12 }}>спорный пробег</div>}
      </td>
      <td data-label="Сделка">
        {r.deal?.bitrix_id
          ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
          : <span className="muted">{r.purpose === 'personal' ? 'личная поездка' : 'без сделки'}</span>}
        {r.vessel?.name && (
          <div style={{ fontSize: 12 }}>
            <EntityLink kind="vessel" id={r.vessel.bitrix_id} title={r.vessel.name} />
          </div>
        )}
      </td>
    </tr>
  )
}
